import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Plus, Search, User, Phone, Mail, FileText, Trash2, Edit2, X, AlertTriangle } from 'lucide-react';

// ── Detección de similitud fonética ───────────────────────
export function normalize(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')  // quitar tildes
    .replace(/[çÇ]/g, 'c')
    .replace(/[ñÑ]/g, 'n')
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Distancia de Levenshtein simplificada
function levenshtein(a, b) {
  if (!a || !b) return Math.max((a||'').length, (b||'').length);
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m+1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i-1] === b[j-1] ? dp[i-1][j-1] : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
  return dp[m][n];
}

// Retorna similitud 0-1 entre dos nombres
function nameSimilarity(a, b) {
  const na = normalize(a), nb = normalize(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  // Coincidencia exacta normalizada
  if (na.includes(nb) || nb.includes(na)) return 0.9;
  // Levenshtein sobre el nombre completo
  const dist = levenshtein(na, nb);
  const maxLen = Math.max(na.length, nb.length);
  const score = 1 - dist / maxLen;
  // Bonus: si comparten apellido (primera palabra)
  const wordA = na.split(' '), wordB = nb.split(' ');
  const sharedWords = wordA.filter(w => w.length > 2 && wordB.includes(w)).length;
  return Math.min(1, score + sharedWords * 0.1);
}

export function findSimilarClients(name, clients, threshold = 0.72) {
  if (!name || !clients?.length) return [];
  return clients
    .map(c => ({ client: c, score: nameSimilarity(name, c.name) }))
    .filter(x => x.score >= threshold)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);
}

function ClientModal({ client, onClose }) {
  const { addClient, updateClient, clients, claims, updateClaim } = useApp();
  const isEdit = !!client;
  const [form, setForm] = useState(client || { name: '', dni: '', phone: '', email: '', address: '', notes: '' });
  const [similarWarning, setSimilarWarning] = useState(null); // null | [matches]
  const [forceCreate, setForceCreate] = useState(false);
  const [siniestrosSueltos, setSiniestrosSueltos] = useState([]); // siniestros sin cliente con nombre similar
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Busca siniestros sin clientId cuyo nombre libre o título se parezca
  const buscarSiniestrosSueltos = (nombre) => {
    if (!nombre || nombre.length < 4) return [];
    const sueltos = (claims || []).filter(c => !c.clientId);
    return sueltos.filter(c => {
      const texto = `${c.clientNameFree || ''} ${c.title || ''} ${c.description || ''}`;
      return findSimilarClients(nombre, [{ id: c.id, name: texto }], 0.5).length > 0;
    });
  };

  // Buscar similares cuando cambia el nombre
  const handleNameChange = (val) => {
    set('name', val);
    if (!isEdit && val.length >= 4) {
      const similar = findSimilarClients(val, clients);
      setSimilarWarning(similar.length > 0 ? similar : null);
      setSiniestrosSueltos(buscarSiniestrosSueltos(val));
    } else {
      setSimilarWarning(null);
      setSiniestrosSueltos([]);
    }
    setForceCreate(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    // Si hay similares y no confirmó, mostrar alerta en vez de guardar
    if (!isEdit && similarWarning && !forceCreate) {
      setForceCreate(false); // mantener el warning visible
      return;
    }
    let nuevoCliente;
    if (isEdit) { await updateClient(client.id, form); }
    else { nuevoCliente = await addClient(form); }
    // Asociar automáticamente los siniestros sueltos detectados al cliente nuevo
    if (nuevoCliente && siniestrosSueltos.length > 0) {
      for (const s of siniestrosSueltos) {
        await updateClaim(s.id, { clientId: nuevoCliente.id });
      }
    }
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>{isEdit ? 'Editar cliente' : 'Nuevo cliente'}</h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Nombre completo / Razón social *</label>
                <input value={form.name} onChange={e => handleNameChange(e.target.value)} required placeholder="Ej: Juan Pérez" />
              </div>
              <div className="form-group">
                <label className="form-label">CI / RUT / CUIT</label>
                <input value={form.dni} onChange={e => set('dni', e.target.value)} placeholder="Ej: 1.234.567-8" />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono / WhatsApp</label>
                <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="Ej: 099 123 456" />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="correo@ejemplo.com" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Dirección</label>
              <input value={form.address} onChange={e => set('address', e.target.value)} placeholder="Calle, número, ciudad" />
            </div>
            <div className="form-group">
              <label className="form-label">Notas internas</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} placeholder="Preferencias, observaciones..." />
            </div>

            {/* ── Aviso: siniestros sueltos con nombre similar ── */}
            {!isEdit && siniestrosSueltos.length > 0 && (
              <div style={{ background: 'var(--accent-dim)', border: '1px solid var(--accent)', borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <AlertTriangle size={16} color="var(--accent)" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>
                    Siniestro{siniestrosSueltos.length > 1 ? 's' : ''} sin cliente detectado{siniestrosSueltos.length > 1 ? 's' : ''}
                  </span>
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                  Al guardar, se asociará{siniestrosSueltos.length > 1 ? 'n' : ''} automáticamente a este cliente:
                </p>
                {siniestrosSueltos.map(s => (
                  <div key={s.id} style={{ fontSize: 12, color: 'var(--text-primary)', padding: '4px 0' }}>
                    • {s.title || s.clientNameFree} {s.claimNumber && `(${s.claimNumber})`}
                  </div>
                ))}
              </div>
            )}

            {/* ── Alerta de similitud ── */}
            {!isEdit && similarWarning && !forceCreate && (
              <div style={{ background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.4)', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <AlertTriangle size={16} color="var(--amber)" />
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--amber)' }}>
                    ¿Ya existe este cliente?
                  </span>
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                  Encontramos clientes con nombre similar. ¿Querés usar uno de estos en vez de crear uno nuevo?
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                  {similarWarning.map(({ client: c, score }) => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', justify: 'space-between', background: 'var(--bg-input)', borderRadius: 8, padding: '8px 12px', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {[c.phone, c.email, c.dni].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 700, background: 'rgba(245,166,35,0.15)', padding: '2px 7px', borderRadius: 6 }}>
                        {Math.round(score * 100)}% similar
                      </span>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => { onClose(); }}>
                        Usar este
                      </button>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => setSimilarWarning(null)}>
                    Volver a editar
                  </button>
                  <button type="button" className="btn btn-amber btn-sm" style={{ flex: 1, background: 'rgba(245,166,35,0.15)', color: 'var(--amber)', border: '1px solid rgba(245,166,35,0.4)' }}
                    onClick={() => setForceCreate(true)}>
                    Crear igual (es otro cliente)
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer — ocultar si hay warning sin confirmar */}
          {(!similarWarning || forceCreate) && (
            <div className="modal-footer">
              <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
              <button type="submit" className="btn btn-primary">{isEdit ? 'Guardar cambios' : 'Crear cliente'}</button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default function ClientsPage() {
  const { clients, policies, deleteClient } = useApp();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [modal, setModal] = useState(null);

  const getPolicyCount = (id) => policies.filter(p => p.clientId === id && (p.status === 'active' || p.status === 'endorsed')).length;
  const getTotalPremium = (id) => policies.filter(p => p.clientId === id && (p.status === 'active' || p.status === 'endorsed')).reduce((s, p) => s + (Number(p.premium) || 0), 0);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };

  const filtered = clients
    .filter(c =>
      normalize(c.name).includes(normalize(search)) ||
      c.dni?.includes(search) ||
      normalize(c.email).includes(normalize(search)) ||
      c.phone?.includes(search)
    )
    .sort((a, b) => {
      let vA, vB;
      switch (sortBy) {
        case 'name':    vA = a.name?.toLowerCase() || ''; vB = b.name?.toLowerCase() || ''; break;
        case 'policies': vA = getPolicyCount(a.id); vB = getPolicyCount(b.id); break;
        case 'premium': vA = getTotalPremium(a.id); vB = getTotalPremium(b.id); break;
        default: vA = a.name?.toLowerCase() || ''; vB = b.name?.toLowerCase() || '';
      }
      if (vA < vB) return sortDir === 'asc' ? -1 : 1;
      if (vA > vB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const confirmDelete = (c) => {
    if (window.confirm(`¿Eliminar a ${c.name} y todas sus pólizas?`)) deleteClient(c.id);
  };

  const SortBtn = ({ field, label }) => (
    <button onClick={() => handleSort(field)} className="btn btn-secondary btn-sm" style={{
      color: sortBy === field ? 'var(--accent)' : 'var(--text-secondary)',
      borderColor: sortBy === field ? 'var(--accent)' : 'var(--border)',
      background: sortBy === field ? 'var(--accent-dim)' : 'transparent',
    }}>
      {label} {sortBy === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </button>
  );

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clientes</h1>
          <p className="page-subtitle">{clients.length} clientes en cartera</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}><Plus size={15} /> Nuevo cliente</button>
      </div>

      {/* Search + Sort */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, CI, email o teléfono..." style={{ paddingLeft: 38 }} />
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Ordenar:</span>
          <SortBtn field="name" label="A–Z" />
          <SortBtn field="policies" label="Pólizas" />
          <SortBtn field="premium" label="Prima" />
        </div>
      </div>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {filtered.map(c => {
          const polCount = getPolicyCount(c.id);
          return (
            <div key={c.id} className="card card-hover fade-in" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 12 }}
              onClick={() => navigate(`/clients/${c.id}`)}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent)', fontSize: 14
                  }}>
                    {c.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontFamily: 'var(--font-display)', fontSize: 14 }}>{c.name}</div>
                    {c.dni && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>CI: {c.dni}</div>}
                  </div>
                </div>
                <span className="badge badge-blue">{polCount} póliza{polCount !== 1 ? 's' : ''}</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {c.phone && <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text-secondary)' }}><Phone size={11} />{c.phone}</div>}
                {c.email && <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text-secondary)' }}><Mail size={11} />{c.email}</div>}
              </div>

              <div className="divider" style={{ margin: '4px 0' }} />

              <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
                <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => navigate(`/clients/${c.id}`)}>
                  <FileText size={12} /> Ver pólizas
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setModal(c)}><Edit2 size={12} /></button>
                <button className="btn btn-danger btn-sm" onClick={() => confirmDelete(c)}><Trash2 size={12} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
          <User size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p>No se encontraron clientes</p>
          {search && <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={() => setSearch('')}>Limpiar búsqueda</button>}
        </div>
      )}

      {modal && <ClientModal client={modal === 'new' ? null : modal} onClose={() => setModal(null)} />}
    </div>
  );
}
